import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const Tree = ({ node }) => (
  <div className="border-l border-brand-data/50 pl-3 text-sm">
    <div className="py-1"><span className="font-mono text-xs text-muted-foreground">plant</span> {node.name}</div>
    {node.children.map((line) => (
      <div key={line.name} className="ml-5 border-l border-brand-data/50 pl-3">
        <div className="py-1"><span className="font-mono text-xs text-muted-foreground">line</span> {line.name}</div>
        {line.children.map((m) => (
          <div key={m.name} className="ml-5 border-l border-brand-data/50 py-1 pl-3"><span className="font-mono text-xs text-muted-foreground">machine</span> {m.name}</div>
        ))}
      </div>
    ))}
  </div>
);

const Table = ({ cols, rows, testId }) => (
  <div className="overflow-x-auto">
    <table data-testid={testId} className="w-full text-left text-sm">
      <thead><tr>{cols.map(([k, l]) => <th key={k} className="border-b border-border py-2 pr-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{l}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>{cols.map(([k]) => <td key={k} className={`border-b border-border py-2 pr-4 ${r.quality === "Stale" && k === "quality" ? "font-semibold text-destructive" : ""}`}>{String(r[k])}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

const OeeChart = ({ s }) => {
  const data = s.loss_tree.map((l) => ({ asset: l.asset.split(" ")[0], "Shift A": l.minutes[0], "Shift B": l.minutes[1], "Shift C": l.minutes[2] }));
  return (
    <div className="space-y-6">
      <Table testId="capstone-oee-table" cols={[["shift", "Shift"], ["availability", "Avail %"], ["performance", "Perf %"], ["quality", "Qual %"], ["oee", "OEE %"]]} rows={s.oee} />
      <div className="h-64">
        <ResponsiveContainer>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="asset" stroke="hsl(var(--muted-foreground))" fontSize={12} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} label={{ value: "loss min", angle: -90, position: "insideLeft", fontSize: 11 }} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
            <Legend />
            <Bar dataKey="Shift A" fill="hsl(var(--brand-data))" />
            <Bar dataKey="Shift B" fill="hsl(var(--brand-primary))" />
            <Bar dataKey="Shift C" fill="hsl(var(--brand-action))" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const GovernedQuery = () => {
  const [q, setQ] = useState({ asset: "Capper C-302", metric: "micro_stops", role: "Maintenance leader" });
  const [res, setRes] = useState(null);
  const run = async () => {
    try {
      setRes((await api.post("/capstone/query", q)).data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const sel = (k, opts) => (
    <Select value={q[k]} onValueChange={(v) => setQ({ ...q, [k]: v })}>
      <SelectTrigger data-testid={`governed-${k}`} className="h-9 rounded-sm"><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="border border-border bg-card p-5">
      <div className="overline-label">Ask a governed question</div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {sel("asset", ["Line 3", "Filler F-301", "Capper C-302", "Labeler L-303", "Conveyor CV-304"].map((a) => [a, a]))}
        {sel("metric", [["micro_stops", "Micro-stops"], ["torque", "Torque"], ["work_orders", "Work orders"], ["procedure", "Procedure"]])}
        {sel("role", ["Operator", "Maintenance leader", "Plant manager"].map((r) => [r, `As ${r}`]))}
      </div>
      <button data-testid="governed-query-button" onClick={run} className="mt-4 rounded-sm bg-brand-data px-4 py-2 text-sm font-semibold text-white hover:opacity-90">Run governed query</button>
      {res && (
        <div data-testid="governed-query-result" className="mt-5 space-y-2">
          {res.steps.map((s, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${s.ok ? "bg-brand-accent" : "bg-destructive"}`} />
              <div><span className="font-semibold">{i + 1}. {s.step}</span> <span className="text-muted-foreground">— {s.detail}</span></div>
            </div>
          ))}
          {res.evidence.length > 0 && <pre className="mt-3 max-h-48 overflow-auto bg-muted p-3 font-mono text-xs">{JSON.stringify(res.evidence, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
};

export const EvidenceWorkspace = ({ s }) => (
  <Tabs defaultValue="assets" className="border border-border bg-card p-5">
    <TabsList className="flex h-auto flex-wrap justify-start gap-1 rounded-sm bg-muted p-1">
      {[["assets", "Asset graph"], ["oee", "OEE"], ["historian", "Historian"], ["wo", "Work orders"], ["sops", "SOPs"], ["log", "Shift log"]].map(([v, l]) => (
        <TabsTrigger key={v} value={v} data-testid={`evidence-tab-${v}`} className="rounded-sm text-xs">{l}</TabsTrigger>
      ))}
    </TabsList>
    <div className="mt-5">
      <TabsContent value="assets"><Tree node={s.assets} /></TabsContent>
      <TabsContent value="oee"><OeeChart s={s} /></TabsContent>
      <TabsContent value="historian"><Table testId="capstone-historian-table" cols={[["tag", "Tag"], ["asset", "Asset"], ["value", "Value"], ["unit", "Unit"], ["timestamp", "Timestamp (UTC)"], ["quality", "Quality"]]} rows={s.historian} /><p className="mt-3 text-xs text-muted-foreground">{s.data_issues_hint}</p></TabsContent>
      <TabsContent value="wo"><Table testId="capstone-wo-table" cols={[["id", "ID"], ["asset", "Asset"], ["title", "Title"], ["status", "Status"]]} rows={s.work_orders} /></TabsContent>
      <TabsContent value="sops"><div className="space-y-3">{s.sops.map((x) => <div key={x.id} className="border-l-2 border-brand-data pl-3"><div className="text-sm font-semibold">{x.id} · {x.title}</div><p className="text-sm text-muted-foreground">{x.excerpt}</p></div>)}</div></TabsContent>
      <TabsContent value="log"><div className="space-y-3">{s.shift_log.map((x) => <div key={x.time} className="text-sm"><span className="font-mono text-xs text-muted-foreground">{x.time} · {x.author}</span><p>{x.entry}</p></div>)}</div></TabsContent>
    </div>
  </Tabs>
);

export { GovernedQuery };
